import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ColumnMode,
  DatatableComponent,
  SelectionType,
  TableColumn,
} from '@siemens/ngx-datatable';
import { StatusType } from '@siemens/element-ng/common';
import { SI_DATATABLE_CONFIG } from '@siemens/element-ng/datatable';
import { SiActionDialogService } from '@siemens/element-ng/action-modal';
import { Link } from '@siemens/element-ng/link';
import { ModalRef, SiModalService } from '@siemens/element-ng/modal';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { catchError, finalize, forkJoin, map, of, Subscription, timeout } from 'rxjs';
import { CONSTANTS, NOTIFICATIONS } from 'src/app/constants/constant';
import {
  normalizeProjectEntraGroups,
  ProjectEntraGroup,
} from 'src/app/helpers/entra-group';
import { isPersonalProject } from 'src/app/helpers/personal-project';
import { ProjectRole } from 'src/app/models/project';
import {
  canEditProject,
  canManageUsers,
  isOwner,
  normalizeAssignedUserRole,
  normalizeProjectRole,
} from 'src/app/helpers/project-role';
import { ProjectService } from 'src/app/services/project.service';
import { UserService } from 'src/app/services/user.service';
import { CreateProjectComponent } from '../create-project/create-project.component';

type ToastStateName = StatusType;

@Component({
  selector: 'app-project-side-panel',
  templateUrl: './project-side-panel.component.html',
  styleUrl: './project-side-panel.component.scss',
  standalone: false,
})
export class ProjectSidePanelComponent {
  @ViewChild(DatatableComponent) userTable!: DatatableComponent;
  @ViewChild(DatatableComponent) projectSubsTable!: DatatableComponent;

  @Input() folderList: any;
  @Input() sidePanelSubscriptionClusterInfo: any;
  @Input() sidePanelUserInfo: any;
  @Input() selectedProject: any;
  @Input() selectedProjectParentId: any;
  @Input() isLoadingSubscriptions = false;
  @Input() isLoadingClusters = false;
  @Input() isLoadingUsers = false;
  @Output() sidePanelAction = new EventEmitter<any>();

  private ref?: ModalRef<unknown>;
  private modalService = inject(SiModalService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  clusterSubsColumns!: TableColumn[];
  clusterSubsPageNumber = 0;
  ColumnMode = ColumnMode;
  constants = CONSTANTS;
  createProjectForm;
  emailOfUserToAdd = '';
  entraGroupObjectIdToAdd = '';
  selectedRoleToAdd: ProjectRole = 'Reader';
  availableRoles: ProjectRole[] = ['Reader', 'UserAdmin', 'Owner'];
  managedUserRows: any[] = [];
  selectedRoleByEmail: Record<string, ProjectRole> = {};
  isInvalidEmail = false;
  isEditingProject = false;
  isSelectedProjectAPersonalProject = false;
  labelWidth = 140;
  labelWidthDummy = 50;
  notificationConstants = NOTIFICATIONS;
  pageSize = 10;
  projectSubsColumns!: TableColumn[];
  projectSubsPageNumber = 0;
  subscriptionSearchTerm = '';
  clusterSearchTerm = '';
  selectedFolder = 'Root';
  selectedTabIndex = 0;
  selectionType = SelectionType.single;
  subscription?: Subscription;
  tableConfig = SI_DATATABLE_CONFIG;
  
  // Cache for pagination - stores pages by page number
  private subscriptionPageCache = new Map<number, any[]>();
  private clusterPageCache = new Map<number, any[]>();
  private lastSubscriptionSearch = '';
  private lastClusterSearch = '';

  private getUssRolesMap(): Map<string, ProjectRole> {
    const roleMap = new Map<string, ProjectRole>();
    const ussRoles = this.selectedProject?.ussRoles;

    if (!ussRoles) {
      return roleMap;
    }

    const upsertRole = (email: string, roleValue: unknown) => {
      if (!email) {
        return;
      }
      roleMap.set(email.toLowerCase(), normalizeProjectRole(String(roleValue ?? '')));
    };

    if (Array.isArray(ussRoles)) {
      ussRoles.forEach((entry: any) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }

        const key = entry.email || entry.key || entry.user || entry.name;
        const value = entry.role || entry.value;
        if (key && value) {
          upsertRole(String(key), value);
          return;
        }

        Object.entries(entry).forEach(([email, role]) => upsertRole(email, role));
      });
      return roleMap;
    }

    if (typeof ussRoles === 'object') {
      Object.entries(ussRoles).forEach(([email, role]) => upsertRole(email, role));
    }

    return roleMap;
  }

  public get projectEntraGroups(): ProjectEntraGroup[] {
    return normalizeProjectEntraGroups(this.selectedProject);
  }

  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    private toastNotificationService: SiToastNotificationService,
    private siModal: SiActionDialogService
  ) {
    this.createProjectForm = this.buildForm();
  }

  ngOnInit() {
    this.initProjectSubTableColumns();
    this.initClusterSubTableColumns();
  }

  ngOnChanges() {
    this.selectedTabIndex = 0;
    this.isSelectedProjectAPersonalProject = isPersonalProject(this.selectedProject);
    // Clear pagination cache when project changes
    this.subscriptionPageCache.clear();
    this.clusterPageCache.clear();
    this.projectSubsPageNumber = 0;
    this.clusterSubsPageNumber = 0;
    
    // Cache the initial data loaded by the dashboard
    if (this.sidePanelSubscriptionClusterInfo?.projectSubscriptionRows?.length > 0) {
      this.subscriptionPageCache.set(0, this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows);
    }
    if (this.sidePanelSubscriptionClusterInfo?.clusterSubscriptionRows?.length > 0) {
      this.clusterPageCache.set(0, this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows);
    }

    this.rebuildManagedUsers();
  }

  private normalizeMemberRole(user: any): ProjectRole {
    if (!user) {
      return 'Reader';
    }

    const userEmail = user?.email?.toLowerCase();
    const ussRole = userEmail ? this.getUssRolesMap().get(userEmail) : undefined;
    if (ussRole) {
      return ussRole;
    }

    if (
      Array.isArray(this.selectedProject?.projectOwners) &&
      this.selectedProject.projectOwners.some(
        (owner: any) =>
          owner?.email?.toLowerCase() === user?.email?.toLowerCase()
      )
    ) {
      return 'Owner';
    }

    return normalizeAssignedUserRole(
      user?.projectRole || user?.role || user?.wizEffectiveRole
    );
  }

  private mergeOwnersWithMembers(members: any[]): any[] {
    const byEmail = new Map<string, any>();
    const ussRolesMap = this.getUssRolesMap();
    (members ?? []).forEach((member) => {
      if (!member?.email) {
        return;
      }

      byEmail.set(member.email.toLowerCase(), {
        ...member,
        projectRole:
          ussRolesMap.get(member.email.toLowerCase()) ||
          this.normalizeMemberRole(member),
      });
    });

    (this.selectedProject?.projectOwners ?? []).forEach((owner: any) => {
      if (!owner?.email) {
        return;
      }

      const emailKey = owner.email.toLowerCase();
      const existing = byEmail.get(emailKey);
      byEmail.set(emailKey, {
        ...(existing ?? owner),
        name: existing?.name || owner?.name || owner?.email,
        email: owner.email,
        projectRole: ussRolesMap.get(emailKey) || ('Owner' as ProjectRole),
      });
    });

    return Array.from(byEmail.values());
  }

  private rebuildManagedUsers(): void {
    const mergedUsers = this.mergeOwnersWithMembers(this.sidePanelUserInfo?.tempUserRows ?? []);
    this.managedUserRows = mergedUsers;
    this.sidePanelUserInfo = {
      ...(this.sidePanelUserInfo ?? {}),
      userRows: mergedUsers,
      tempUserRows: mergedUsers,
    };

    this.selectedRoleByEmail = {};
    this.managedUserRows.forEach((user) => {
      if (user?.email) {
        this.selectedRoleByEmail[user.email] = this.normalizeMemberRole(user);
      }
    });
  }

  private showToast(
    state: ToastStateName,
    title: string,
    message: string,
    disableAutoClose?: boolean,
    disableManualClose?: boolean,
    timeout?: number,
    action?: Link
  ) {
    this.toastNotificationService.showToastNotification({
      state,
      title,
      message,
      disableAutoClose,
      disableManualClose,
      timeout,
      action,
    });
  }

  private initProjectSubTableColumns(): void {
    this.projectSubsColumns = [
      {
        prop: 'name',
        name: 'Name',
        minWidth: 300,
        maxWidth: 500,
        resizeable: true,
        canAutoResize: true,
        checkboxable: false,
        headerCheckboxable: false,
      },
      {
        prop: 'externalId',
        name: 'Subscription Id',
        minWidth: 400,
        maxWidth: 500,
        resizeable: true,
        canAutoResize: true,
      },
    ];
  }

  private initClusterSubTableColumns(): void {
    this.clusterSubsColumns = [
      {
        prop: 'name',
        name: 'Name',
        minWidth: 200,
        maxWidth: 280,
        resizeable: true,
        canAutoResize: true,
        sortable: true,
        headerCheckboxable: false,
      },
      {
        prop: 'externalId',
        name: 'Cluster Id',
        minWidth: 200,
        maxWidth: 600,
        resizeable: true,
        sortable: true,
        canAutoResize: true,
      },
    ];
  }

  private buildForm() {
    return new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      businessUnit: new FormControl(''),
      newProjectOwner: new FormControl(''),
      subscriptions: new FormControl(['']),
      isFolder: new FormControl(),
    });
  }

  private buildEditProjectData(projectData?: any): any {
    const currentProject = this.selectedProject ?? {};

    const currentSubscriptions = Array.isArray(currentProject?.cloudAccountLinks)
      ? currentProject.cloudAccountLinks
      : [];
    const currentClusters = Array.isArray(currentProject?.kubernetesClusters)
      ? currentProject.kubernetesClusters
      : [];

    const sidePanelSubscriptions = Array.isArray(
      this.sidePanelSubscriptionClusterInfo?.projectSubscriptionRows
    )
      ? this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows
      : [];
    const sidePanelClusters = Array.isArray(
      this.sidePanelSubscriptionClusterInfo?.clusterSubscriptionRows
    )
      ? this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows
      : [];

    const apiSubscriptions = Array.isArray(projectData?.cloudAccountLinks)
      ? projectData.cloudAccountLinks
      : [];
    const apiClusters = Array.isArray(projectData?.kubernetesClusters)
      ? projectData.kubernetesClusters
      : [];

    return {
      ...currentProject,
      ...(projectData ?? {}),
      cloudAccountLinks:
        apiSubscriptions.length > 0
          ? apiSubscriptions
          : currentSubscriptions.length > 0
          ? currentSubscriptions
          : sidePanelSubscriptions,
      kubernetesClusters:
        apiClusters.length > 0
          ? apiClusters
          : currentClusters.length > 0
          ? currentClusters
          : sidePanelClusters,
    };
  }

  private loadAssignmentsForEdit(projectId: string, baseProject: any): void {
    forkJoin({
      subscriptions: this.projectService
        .getProjectSubscriptions(projectId, 0, 1000)
        .pipe(
          map((response: any) => response?.items || []),
          catchError(() => of([]))
        ),
      clusters: this.projectService
        .getProjectClusters(projectId, 0, 1000)
        .pipe(
          map((response: any) => response?.items || []),
          catchError(() => of([]))
        ),
    }).subscribe(({ subscriptions, clusters }) => {
      const hydratedProject = {
        ...baseProject,
        cloudAccountLinks:
          subscriptions.length > 0
            ? subscriptions
            : baseProject?.cloudAccountLinks || [],
        kubernetesClusters:
          clusters.length > 0 ? clusters : baseProject?.kubernetesClusters || [],
      };

      this.selectedProject = hydratedProject;
      this.openModal(CreateProjectComponent, 'modal-lg', hydratedProject);
    });
  }

  public editProject() {
    if (isPersonalProject(this.selectedProject)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.projectCannotBeEdited,
        this.notificationConstants.cantEditPersonalProject
      );
      return;
    }

    if (!this.canEditSelectedProject()) {
      return;
    }

    if (!this.selectedProject || !this.selectedProject.id) {
      console.error('No selected project or project ID');
      this.showToast(
        'warning' as ToastStateName,
        'Error',
        'No project selected for editing'
      );
      return;
    }

    this.projectService
      .getProject(this.selectedProject.id)
      .pipe(timeout(20000))
      .subscribe({
        next: (projectData: any) => {
          const hydratedProject = this.buildEditProjectData(projectData);
          this.loadAssignmentsForEdit(this.selectedProject.id, hydratedProject);
        },
        error: () => {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            'Could not refresh project details. Opening editor with current data.'
          );
          this.loadAssignmentsForEdit(
            this.selectedProject.id,
            this.buildEditProjectData()
          );
        },
      });
  }

  private openModal(template: any, modalClass?: string, projectData?: any) {
      this.ref?.hide();
   
    
   
    this.ref = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: true,
      animated: true,
      class: modalClass,
      ariaLabelledBy: 'create -project-modal',
      inputValues: {
        isEditingProject: true,
        selectedProject: this.buildEditProjectData(projectData || this.selectedProject), // Preserve selected subscriptions/clusters for preselection.
        folderList: this.folderList,
        initialSubscriptionCount: this.sidePanelSubscriptionClusterInfo?.totalSubscriptions || 0,
        initialClusterCount: this.sidePanelSubscriptionClusterInfo?.totalClusters || 0,
      },
    });

    const createProject = this.ref.content as CreateProjectComponent;
    createProject.onClose.subscribe((response: any) => {
      if (response) {
        this.ref?.hide();
      }
    });

    createProject.onSave.subscribe((response: any) => {
      if (response) {
        const projectId = this.selectedProject?.id;
        if (!projectId) {
          this.ref?.hide();
          this.sidePanelAction.emit(response?.changed ? response : { changed: true });
          return;
        }

        this.projectService
          .getProject(projectId)
          .pipe(timeout(20000))
          .subscribe({
            next: (projectData: any) => {
              const updatedProject = {
                ...this.selectedProject,
                ...projectData,
                ...(response?.project || {}),
                subscriptionCount:
                  response?.totalSubscriptions ??
                  response?.project?.subscriptionCount ??
                  this.selectedProject?.subscriptionCount ??
                  0,
                kubernetesClusterCount:
                  response?.totalClusters ??
                  response?.project?.kubernetesClusterCount ??
                  this.selectedProject?.kubernetesClusterCount ??
                  0,
              };

              if (projectData) {
                this.selectedProject = updatedProject;
              }
              this.sidePanelSubscriptionClusterInfo = {
                ...this.sidePanelSubscriptionClusterInfo,
                totalSubscriptions: updatedProject.subscriptionCount,
                totalClusters: updatedProject.kubernetesClusterCount,
                projectSubscriptionRows:
                  response?.project?.cloudAccountLinks ||
                  this.sidePanelSubscriptionClusterInfo?.projectSubscriptionRows ||
                  [],
                projectTempSubRows:
                  response?.project?.cloudAccountLinks ||
                  this.sidePanelSubscriptionClusterInfo?.projectTempSubRows ||
                  [],
                clusterSubscriptionRows:
                  response?.project?.kubernetesClusters ||
                  this.sidePanelSubscriptionClusterInfo?.clusterSubscriptionRows ||
                  [],
                clusterTempSubRows:
                  response?.project?.kubernetesClusters ||
                  this.sidePanelSubscriptionClusterInfo?.clusterTempSubRows ||
                  [],
              };
              this.ref?.hide();
              this.sidePanelAction.emit(
                response?.changed
                  ? {
                      ...response,
                      project: updatedProject,
                    }
                  : { changed: true }
              );
            },
            error: () => {
              this.ref?.hide();
              this.sidePanelAction.emit(response?.changed ? response : { changed: true });
            },
          });
      }
    });
  }

  public setAccessReview() {
    if (isPersonalProject(this.selectedProject) || !this.canEditSelectedProject()) {
      return;
    }

    this.subscription = this.siModal.showActionDialog({ type: 'confirmation', message: 'Have the granted access permissions have been reviewed and comply with the need-to-know principle for the project ' +
        this.selectedProject!.name +
        '?', heading: 'Confirm Access Review' })
      .subscribe((confirmation: any) => {
        switch (confirmation) {
          case 'confirm':
            this.isLoadingUsers = true;
            this.projectService
              .updateAccessReview(this.selectedProject!.id)
              .subscribe({
                next: (response: any) => {
                  this.showToast(
                    this.notificationConstants.success as ToastStateName,
                    this.notificationConstants.successCap,
                    this.notificationConstants.accessReviewUpdate +
                      this.selectedProject!.name +
                      this.notificationConstants.dot
                  );
                  this.getUsers(this.selectedProject!.id);
                },
                error: (err: any) => {
                  this.showToast(
                    this.notificationConstants.warning as ToastStateName,
                    this.notificationConstants.error,
                    err.error
                  );
                  this.isLoadingUsers = false;
                },
              });

            break;
          case 'decline':
            break;
        }
      });
  }

  public confirmProjectDeletion() {
    if (isPersonalProject(this.selectedProject)) {
      return;
    }

    if (!this.canEditSelectedProject()) {
      return;
    }

    this.subscription = this.siModal.showActionDialog({ type: 'delete-confirm', message: this.notificationConstants.canBeDeleted +
        this.selectedProject?.name +
        this.notificationConstants.qMark, heading: this.notificationConstants.actionUndone, deleteBtnName: this.notificationConstants.delete })
      .subscribe((confirmation) => {
        switch (confirmation) {
          case 'delete':
            this.projectService
              .deleteProject(this.selectedProject!.id)
              .subscribe(
                (response) => {
                  this.sidePanelAction.emit({ changed: true });
                  this.showToast(
                    this.notificationConstants.success as ToastStateName,
                    this.notificationConstants.successCap,
                    this.notificationConstants.projectDeleted
                  );
                },
                (err) => {
                  this.showToast(
                    this.notificationConstants.danger as ToastStateName,
                    this.notificationConstants.error,
                    err.error['detail']
                  );
                }
              );
            break;
          case 'cancel':
            break;
        }
      });
  }

  public updateUserFilter(event: any) {
    const val = event.target.value.toLowerCase();
    const tempProjects = (this.sidePanelUserInfo?.tempUserRows ?? []).filter(
      (e: any) =>
        e.email.toLowerCase().includes(val) ||
        e.name.toLowerCase().includes(val) ||
        !val
    );
    this.sidePanelUserInfo.userRows = tempProjects;
    this.userTable.offset = 0;
  }

  public confirmRemoveUser(email: string) {
    if (!this.canManageSelectedProjectUsers()) {
      return;
    }

    this.subscription = this.siModal.showActionDialog({ type: 'delete-confirm', message: 'Do you want to remove ' +
        email +
        ' from ' +
        this.selectedProject!.name +
        '?', heading: 'Remove ' + email })
      .subscribe((confirmation) => {
        switch (confirmation) {
          case 'delete':
            this.isLoadingUsers = true;
            this.userService
              .removeUserFromProject(this.selectedProject!.id, email)
              .subscribe({
                next: (response: any) => {
                  this.showToast(
                    this.notificationConstants.success as ToastStateName,
                    this.notificationConstants.successCap,
                    email +
                      this.notificationConstants.removedFrom +
                      this.selectedProject!.name +
                      '.'
                  );
                  this.refreshProjectMembershipData(this.selectedProject!.id);
                },
                error: (err: any) => {
                  this.showToast(
                    this.notificationConstants.warning as ToastStateName,
                    this.notificationConstants.error,
                    err.error
                  );
                  this.isLoadingUsers = false;
                },
              });

            break;
          case 'cancel':
            break;
        }
      });
  }

  private getUsers(projectId: string, showLoading: boolean = true) {
    if (showLoading) {
      this.isLoadingUsers = true;
    }

    this.userService
      .getProjectUsers(projectId, true)
      .pipe(
        timeout(20000),
        finalize(() => {
          if (showLoading) {
            this.isLoadingUsers = false;
          }
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (data: any) => {
          this.selectedProject = {
            ...this.selectedProject,
            entraGroups: data.entraGroups ?? this.selectedProject?.entraGroups,
            entraGroupIds:
              data.entraGroupIds ?? this.selectedProject?.entraGroupIds,
          };
          this.sidePanelUserInfo.userRows = data.assignedUsersList;
          this.sidePanelUserInfo.tempUserRows = data.assignedUsersList;
          this.rebuildManagedUsers();
          if (data.lastAccessReviewAt === null) {
            this.sidePanelUserInfo.projectLastAccessReviewedAt =
              'No Yet Reviewed';
          } else {
            this.sidePanelUserInfo.projectLastAccessReviewedAt = new Date(
              parseInt(data.lastAccessReviewAt) * 1000
            ).toDateString();
          }

          this.sidePanelUserInfo.projectLastAccessReviewedBy =
            data.lastAccessReviewBy;
          this.changeDetectorRef.detectChanges();
        },
        error: (err) => {
          this.showToast(
            this.notificationConstants.danger as ToastStateName,
            this.notificationConstants.error,
            this.notificationConstants.fetchUsersError
          );
        },
      });
  }

  public addUserToProject() {
    if (!this.canManageSelectedProjectUsers()) {
      return;
    }

    this.isInvalidEmail = false;
    if (!this.isInputEmailFormat(this.emailOfUserToAdd)) {
      this.isInvalidEmail = true;
      return;
    }
    this.isLoadingUsers = true;
    if (
      this.sidePanelUserInfo.userRows.some(
        (user: any) => user.email === this.emailOfUserToAdd
      )
    ) {
      this.showToast(
        this.notificationConstants.info as ToastStateName,
        this.notificationConstants.alreadyAMember,
        this.emailOfUserToAdd + this.notificationConstants.isAlreadyAMember
      );
      this.isLoadingUsers = false;
      this.emailOfUserToAdd = '';
    } else {
      const roleToAssign = this.getRoleToAssignWhenAdding();
      this.userService
        .addUserToProjectByRole(
          this.selectedProject!.id,
          this.emailOfUserToAdd,
          roleToAssign
        )
        .subscribe({
          next: (response) => {
            this.showToast(
              this.notificationConstants.success as ToastStateName,
              this.notificationConstants.successCap,
              response.email +
                this.notificationConstants.addedTo +
                this.selectedProject!.name +
                this.notificationConstants.dot
            );
            this.refreshProjectMembershipData(this.selectedProject!.id);
            this.emailOfUserToAdd = '';
            this.selectedRoleToAdd = 'Reader';
          },
          error: (err) => {
            console.log('Add owner error:', err);
            const errorTitle =
              err?.error?.title ||
              err?.statusText ||
              (typeof err?.error === 'string' ? err.error : null) ||
              this.notificationConstants.error ||
              'Error';
            const errorMessage =
              err?.error?.detail ||
              err?.error?.message ||
              err?.message ||
              (typeof err?.error === 'string' ? err.error : null) ||
              this.notificationConstants.unableToFindUser ||
              'An error occurred. Please try again.';

            this.showToast(
              this.notificationConstants.danger as ToastStateName,
              this.notificationConstants.error +
                this.notificationConstants.colon +
                String(errorTitle),
              String(errorMessage)
            );
            this.emailOfUserToAdd = '';
            this.isLoadingUsers = false;
          },
        });
    }
  }

  public addEntraGroupToProject() {
    if (!this.canManageSelectedProjectUsers()) {
      return;
    }

    const groupObjectId = this.entraGroupObjectIdToAdd.trim();
    if (!this.isValidGuid(groupObjectId)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.invalidInput,
        this.constants.invalidEntraGroupId
      );
      return;
    }

    if (
      this.projectEntraGroups.some(
        (assignedGroup) =>
          assignedGroup.groupObjectId.toLowerCase() === groupObjectId.toLowerCase()
      )
    ) {
      this.showToast(
        this.notificationConstants.info as ToastStateName,
        this.notificationConstants.alreadyAGroup,
        groupObjectId + this.notificationConstants.isAlreadyAGroup
      );
      this.entraGroupObjectIdToAdd = '';
      return;
    }

    this.isLoadingUsers = true;
    this.userService
      .addEntraGroupToProject(this.selectedProject!.id, groupObjectId)
      .subscribe({
        next: () => {
          this.showToast(
            this.notificationConstants.success as ToastStateName,
            this.notificationConstants.successCap,
            'Entra ID Group' +
              this.notificationConstants.addedTo +
              this.selectedProject!.name +
              this.notificationConstants.dot
          );
          this.entraGroupObjectIdToAdd = '';
          this.refreshProjectMembershipData(this.selectedProject!.id);
        },
        error: (err) => {
          const errorTitle =
            err?.error?.title ||
            err?.statusText ||
            (typeof err?.error === 'string' ? err.error : null) ||
            this.notificationConstants.error;
          const errorMessage =
            err?.error?.detail ||
            err?.error?.message ||
            err?.message ||
            (typeof err?.error === 'string' ? err.error : null) ||
            'An error occurred. Please try again.';

          this.showToast(
            this.notificationConstants.danger as ToastStateName,
            this.notificationConstants.error +
              this.notificationConstants.colon +
              String(errorTitle),
            String(errorMessage)
          );
          this.isLoadingUsers = false;
        },
      });
  }

  public removeEntraGroupMapping(group: ProjectEntraGroup) {
    const groupObjectId = group?.groupObjectId?.trim();
    if (!this.canManageSelectedProjectUsers() || !groupObjectId) {
      return;
    }

    const groupDisplayName = group.groupName
      ? group.groupName + ' (' + groupObjectId + ')'
      : groupObjectId;

    this.subscription = this.siModal
      .showActionDialog({
        type: 'delete-confirm',
        message:
          'Do you want to remove Entra ID Group ' +
          groupDisplayName +
          ' from ' +
          this.selectedProject!.name +
          '?',
        heading: 'Remove Entra ID Group',
      })
      .subscribe((confirmation) => {
        if (confirmation !== 'delete') {
          return;
        }

        this.isLoadingUsers = true;
        this.userService
          .removeEntraGroupMapping(this.selectedProject!.id, groupObjectId)
          .subscribe({
          next: () => {
            this.showToast(
              this.notificationConstants.success as ToastStateName,
              this.notificationConstants.successCap,
              'Entra ID Group mapping removed.'
            );
            this.refreshProjectMembershipData(this.selectedProject!.id);
          },
          error: (err) => {
            const errorMessage =
              err?.error?.detail ||
              err?.error?.message ||
              err?.message ||
              'An error occurred. Please try again.';

            this.showToast(
              this.notificationConstants.danger as ToastStateName,
              this.notificationConstants.error,
              String(errorMessage)
            );
            this.isLoadingUsers = false;
          },
        });
      });
  }

  private isInputEmailFormat(email: string) {
    const regexExp =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;
    return regexExp.test(email);
  }

  private isValidGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value.trim()
    );
  }

  private refreshProjectMembershipData(projectId: string, showUserLoading: boolean = true) {
    this.getUsers(projectId, showUserLoading);

    this.projectService
      .getProject(projectId, true)
      .pipe(timeout(20000))
      .subscribe({
        next: (project: any) => {
          if (project) {
            this.selectedProject = {
              ...this.selectedProject,
              ...project,
            };
            this.rebuildManagedUsers();
          }
        },
        error: () => {},
      });
  }

  public searchSubscriptionsFromInput() {
    this.searchSubscriptions(this.subscriptionSearchTerm);
  }

  public clearSubscriptionSearch() {
    this.subscriptionSearchTerm = '';
    this.searchSubscriptions('');
  }

  public searchSubscriptions(searchTerm: string) {
    if (!this.selectedProject?.id) return;

    // Clear cache if search term changed
    if (this.lastSubscriptionSearch !== searchTerm) {
      this.subscriptionPageCache.clear();
      this.lastSubscriptionSearch = searchTerm;
    }

    this.projectSubsPageNumber = 0;
    this.isLoadingSubscriptions = true;
    this.projectService.getProjectSubscriptions(this.selectedProject.id, 0, 10, searchTerm).subscribe({
      next: (response) => {
        const subscriptions = response.items || [];
        this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.subsTempSubRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.totalSubscriptions = response.totalCount || 0;
        // Cache page 0
        this.subscriptionPageCache.set(0, subscriptions);
        this.projectSubsTable.offset = 0;
        this.isLoadingSubscriptions = false;
      },
      error: (err) => {
        console.error('Search subscriptions error:', err);
        this.isLoadingSubscriptions = false;
      },
      complete: () => {
        // Ensure loading is always stopped
        this.isLoadingSubscriptions = false;
      }
    });
  }

  public onSubscriptionPageChange(page: number) {
    if (!this.selectedProject?.id) return;

    this.projectSubsPageNumber = page - 1;
    
    // Check cache first
    const cachedPage = this.subscriptionPageCache.get(this.projectSubsPageNumber);
    if (cachedPage) {
      this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows = cachedPage;
      this.sidePanelSubscriptionClusterInfo.subsTempSubRows = cachedPage;
      return;
    }

    // Fetch from API if not cached
    this.isLoadingSubscriptions = true;
    this.projectService.getProjectSubscriptions(this.selectedProject.id, this.projectSubsPageNumber, 10, this.subscriptionSearchTerm).subscribe({
      next: (response) => {
        const subscriptions = response.items || [];
        this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.subsTempSubRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.totalSubscriptions = response.totalCount || 0;
        // Cache this page
        this.subscriptionPageCache.set(this.projectSubsPageNumber, subscriptions);
        this.isLoadingSubscriptions = false;
      },
      error: (err) => {
        console.error('Page change subscriptions error:', err);
        this.isLoadingSubscriptions = false;
      },
      complete: () => {
        // Ensure loading is always stopped
        this.isLoadingSubscriptions = false;
      }
    });
  }

  public searchClustersFromInput() {
    this.searchClusters(this.clusterSearchTerm);
  }

  public clearClusterSearch() {
    this.clusterSearchTerm = '';
    this.searchClusters('');
  }

  public searchClusters(searchTerm: string) {
    if (!this.selectedProject?.id) return;

    // Clear cache if search term changed
    if (this.lastClusterSearch !== searchTerm) {
      this.clusterPageCache.clear();
      this.lastClusterSearch = searchTerm;
    }

    this.clusterSubsPageNumber = 0;
    this.isLoadingClusters = true;
    this.projectService.getProjectClusters(this.selectedProject.id, 0, 10, searchTerm).subscribe({
      next: (response) => {
        const clusters = response.items || [];
        this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows = clusters;
        this.sidePanelSubscriptionClusterInfo.clusterTempSubRows = clusters;
        this.sidePanelSubscriptionClusterInfo.totalClusters = response.totalCount || 0;
        // Cache page 0
        this.clusterPageCache.set(0, clusters);
        this.projectSubsTable.offset = 0;
        this.isLoadingClusters = false;
      },
      error: (err) => {
        console.error('Search clusters error:', err);
        this.isLoadingClusters = false;
      },
      complete: () => {
        // Ensure loading is always stopped
        this.isLoadingClusters = false;
      }
    });
  }

  public onClusterPageChange(page: number) {
    if (!this.selectedProject?.id) return;

    this.clusterSubsPageNumber = page - 1;
    
    // Check cache first
    const cachedPage = this.clusterPageCache.get(this.clusterSubsPageNumber);
    if (cachedPage) {
      this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows = cachedPage;
      this.sidePanelSubscriptionClusterInfo.clusterTempSubRows = cachedPage;
      return;
    }

    // Fetch from API if not cached
    this.isLoadingClusters = true;
    this.projectService.getProjectClusters(this.selectedProject.id, this.clusterSubsPageNumber, 10, this.clusterSearchTerm).subscribe({
      next: (response) => {
        const clusters = response.items || [];
        this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows = clusters;
        this.sidePanelSubscriptionClusterInfo.clusterTempSubRows = clusters;
        this.sidePanelSubscriptionClusterInfo.totalClusters = response.totalCount || 0;
        // Cache this page
        this.clusterPageCache.set(this.clusterSubsPageNumber, clusters);
        this.isLoadingClusters = false;
      },
      error: (err) => {
        console.error('Page change clusters error:', err);
        this.isLoadingClusters = false;
      },
      complete: () => {
        // Ensure loading is always stopped
        this.isLoadingClusters = false;
      }
    });
  }

  public getSelectedProjectRole(): ProjectRole {
    return normalizeProjectRole(this.selectedProject?.projectRole);
  }

  public getRoleDisplayLabel(role: string | ProjectRole | undefined): string {
    const normalizedRole = normalizeProjectRole(role || 'Reader');

    switch (normalizedRole) {
      case 'UserAdmin':
        return 'UserAdmin';
      case 'Owner':
        return 'Owner';
      case 'Reader':
      default:
        return 'Reader';
    }
  }

  public isReaderSelectedProject(): boolean {
    return this.getSelectedProjectRole() === 'Reader';
  }

  public getAssignableRoles(): ProjectRole[] {
    return this.canCurrentUserAssignUserAdmin() ? this.availableRoles : ['Reader'];
  }

  public getRoleToAssignWhenAdding(): ProjectRole {
    return this.canCurrentUserAssignUserAdmin() ? this.selectedRoleToAdd : 'Reader';
  }

  public getRoleOptionsForUser(user: any): ProjectRole[] {
    if (this.canCurrentUserAssignUserAdmin()) {
      return this.availableRoles;
    }

    const currentRole = this.normalizeMemberRole(user);
    if (currentRole === 'Reader') {
      return ['Reader'];
    }

    return [currentRole];
  }

  public canEditRoleForUser(user: any): boolean {
    if (!this.canManageSelectedProjectUsers()) {
      return false;
    }

    if (this.canCurrentUserAssignUserAdmin()) {
      return true;
    }

    return this.normalizeMemberRole(user) === 'Reader';
  }

  public updateUserRole(user: any): void {
    if (!this.selectedProject?.id || !user?.email || !this.canManageSelectedProjectUsers()) {
      return;
    }

    const currentRole = this.normalizeMemberRole(user);
    const targetRole = this.selectedRoleByEmail[user.email] || currentRole;

    if (currentRole === targetRole) {
      return;
    }

    if (!this.canCurrentUserAssignUserAdmin() && targetRole !== 'Reader') {
      return;
    }

    this.isLoadingUsers = true;
    this.userService
      .addUserToProjectByRole(this.selectedProject.id, user.email, targetRole)
      .subscribe({
        next: () => {
          this.showToast(
            this.notificationConstants.success as ToastStateName,
            this.notificationConstants.successCap,
            user.email + ' role updated to ' + targetRole + '.'
          );
          this.refreshProjectMembershipData(this.selectedProject.id);
        },
        error: (err: any) => {
          this.isLoadingUsers = false;
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            err?.error?.detail || err?.error || 'Unable to update user role.'
          );
        },
      });
  }

  public canEditSelectedProject(): boolean {
    return canEditProject(this.getSelectedProjectRole());
  }

  public canManageSelectedProjectUsers(): boolean {
    return canManageUsers(this.getSelectedProjectRole());
  }

  public canCurrentUserAssignUserAdmin(): boolean {
    return isOwner(this.getSelectedProjectRole());
  }

  public assignOwnerAsUserAdmin(email: string): void {
    if (!this.selectedProject || !this.canCurrentUserAssignUserAdmin()) {
      return;
    }

    this.subscription = this.siModal
      .showActionDialog({
        type: 'confirmation',
        heading: 'Assign UserAdmin',
        message:
          'Do you want to change ' +
          email +
          ' from Owner to UserAdmin in ' +
          this.selectedProject.name +
          '?',
      })
      .subscribe((confirmation) => {
        if (confirmation !== 'confirm') {
          return;
        }

        this.isLoadingUsers = true;
        this.userService
          .addUserToProjectByRole(this.selectedProject.id, email, 'UserAdmin')
          .subscribe({
            next: () => {
              this.showToast(
                this.notificationConstants.success as ToastStateName,
                this.notificationConstants.successCap,
                email + ' has been reassigned as UserAdmin.'
              );
              this.refreshProjectMembershipData(this.selectedProject.id);
            },
            error: (err: any) => {
              this.isLoadingUsers = false;
              this.showToast(
                this.notificationConstants.warning as ToastStateName,
                this.notificationConstants.error,
                err?.error?.detail || err?.error || 'Unable to assign UserAdmin role.'
              );
            },
          });
      });
  }

}
